import { SelectVisitComponent, SelectVisitData, SelectVisitFlow, SelectVisitResult } from '../_dialogs/select-visit/select-visit.component';
import { Subject } from '../_interfaces/subject';
import { Visit } from '../_interfaces/visit';
import { PatientSource } from '../shared/components/patient-selector/patient-selector.component';
import { DialogService } from '../shared/components/dialog/dialog.service';
import { AppDialogRef } from '../shared/components/dialog/dialog-ref';

/**
 * Only searched patients carry the HL7 `_patient.id` that the visits endpoint is keyed on;
 * scheduled rows expose their `_patient_list` id instead, so they skip the visit step.
 */
export function canSelectVisit(
  isEnabled: boolean,
  source: PatientSource | null,
  patient: Subject | null
): boolean {
  return isEnabled
    && source === 'search'
    && Number(patient?.id) > 0;
}

export function openSelectVisitDialog(
  dialog: DialogService,
  data: SelectVisitData
): AppDialogRef<SelectVisitComponent, SelectVisitResult> {
  return dialog.open<SelectVisitComponent, SelectVisitData, SelectVisitResult>(SelectVisitComponent, {
    size: 'xl',
    tone: 'container-low',
    data,
  });
}

export interface ContinueWithOptionalVisitParams {
  dialog: DialogService;
  dialogRef: AppDialogRef<any>;
  patient: Subject;
  mode: 'consultation' | 'dictation';
  flow: SelectVisitFlow;
  source: PatientSource | null;
  isVisitSelectionEnabled: boolean;
  onCommit: (visit: Visit | null) => void | Promise<void>;
}

export function continueWithOptionalVisit(params: ContinueWithOptionalVisitParams): void {
  const {
    dialog,
    dialogRef,
    patient,
    mode,
    flow,
    source,
    isVisitSelectionEnabled,
    onCommit,
  } = params;

  if (!canSelectVisit(isVisitSelectionEnabled, source, patient)) {
    void onCommit(null);
    return;
  }

  const visitRef = openSelectVisitDialog(dialog, { patient, mode, flow });
  visitRef.beforeClosed().subscribe((res) => {
    if (res === 'close') {
      dialog.closeWithoutAnimation(dialogRef, 'close');
      return;
    }
    if (res && res !== 'back') {
      void onCommit(res.visit);
    }
  });
  visitRef.afterClosed().subscribe((res) => {
    // Backdrop or Escape arrive as undefined and read as Back. Nested swipe-to-dismiss
    // emits 'close', so this must not revive a parent that is already shutting down.
    if (!res || res === 'back') {
      dialog.show(dialogRef);
    }
  });
}
